import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Trophy, TrendingUp, TrendingDown, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const fmt = (n) => (parseInt(n, 10) || 0).toLocaleString('fr-FR');

const TVMode = () => {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());
  const [view, setView] = useState(0); // 0 = classement, 1 = top journalier

  const fetchData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/dashboard/objectifs-ca-mensuels`);
      setData(r.data);
    } catch (_) { /* silent on TV mode */ }
  }, []);

  useEffect(() => {
    fetchData();
    const i = setInterval(fetchData, 15000);
    return () => clearInterval(i);
  }, [fetchData]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const v = setInterval(() => setView(x => (x + 1) % 2), 15000);
    return () => clearInterval(v);
  }, []);

  // Plein écran fond bleu marine, pas de scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (!data) {
    return (
      <div className="fixed inset-0 bg-[#0B132B] flex items-center justify-center">
        <div className="text-[#FFD700] text-3xl font-bold animate-pulse">PlayCity TV — Chargement...</div>
      </div>
    );
  }

  const sorted = [...(data.commerciaux || [])].sort((a, b) => (b.commissionVariable || 0) - (a.commissionVariable || 0));
  const topToday = [...(data.commerciaux || [])].sort((a, b) => (b.caRealise || 0) - (a.caRealise || 0));

  const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-[#0B132B] via-[#1C2541] to-[#0B132B] text-[#EAEAEA] overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 px-12 py-6 flex items-center justify-between border-b-2 border-[#C6A15B]/30 backdrop-blur-md">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#FFD700]/20 border-2 border-[#FFD700]/40 flex items-center justify-center">
            <Trophy className="w-8 h-8 text-[#FFD700]" />
          </div>
          <div>
            <div className="text-[#FFD700] text-3xl font-black tracking-wider">PlayCity</div>
            <div className="text-[#EAEAEA]/60 text-sm uppercase tracking-[0.3em]">Tableau de bord live</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[#FFD700] text-5xl font-black tabular-nums">{timeStr}</div>
          <div className="text-[#EAEAEA]/60 text-base capitalize">{dateStr}</div>
        </div>
      </div>

      {/* Vue rotative */}
      <div className="absolute inset-0 pt-32 pb-12 px-12">
        {view === 0 ? (
          // Vue 1 : classement commission cumulée
          <div className="h-full flex flex-col">
            <h2 className="text-4xl font-black text-[#FFD700] mb-8 uppercase tracking-wider">
              🏆 Classement {data.moisNom || 'du mois'}
            </h2>
            <div className="flex-1 grid gap-5 content-start">
              {sorted.map((c, i) => {
                const isLeader = i === 0 && (c.commissionVariable || 0) > 0;
                const positive = (c.commissionVariable || 0) >= 0;
                return (
                  <div
                    key={c.id}
                    className={`flex items-center gap-6 p-6 rounded-2xl border-2 transition-all ${
                      isLeader
                        ? 'bg-gradient-to-r from-[#FFD700]/20 via-[#C6A15B]/10 to-transparent border-[#FFD700]'
                        : 'bg-[#0B132B]/60 border-[#C6A15B]/30'
                    }`}
                  >
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-3xl font-black ${
                      i === 0 ? 'bg-[#FFD700] text-[#0B132B]' :
                      i === 1 ? 'bg-[#C0C0C0] text-[#0B132B]' :
                      i === 2 ? 'bg-[#CD7F32] text-[#0B132B]' :
                      'bg-[#1C2541] text-[#EAEAEA]/50'
                    }`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-3xl font-bold truncate">{c.nom}</div>
                      <div className="text-[#EAEAEA]/60 text-sm">CA : {fmt(c.caRealise)} F • Objectif : {fmt(c.objectifCumule)} F</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-4xl font-black tabular-nums ${positive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                        {positive ? '+' : ''}{fmt(c.commissionVariable)} F
                      </div>
                      <div className={`flex items-center gap-1 justify-end text-xs uppercase tracking-wider ${positive ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>
                        {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                        {positive ? 'En avance' : 'À rattraper'}
                      </div>
                    </div>
                  </div>
                );
              })}
              {sorted.length === 0 && (
                <div className="text-center text-[#EAEAEA]/40 text-2xl py-16">Aucun commercial actif ce mois</div>
              )}
            </div>
          </div>
        ) : (
          // Vue 2 : Top CA réalisé du mois
          <div className="h-full flex flex-col">
            <h2 className="text-4xl font-black text-[#FFD700] mb-8 uppercase tracking-wider">
              🚀 Top CA réalisé {data.moisNom || ''}
            </h2>
            <div className="flex-1 grid grid-cols-2 gap-6 content-start">
              {topToday.slice(0, 4).map((c, i) => (
                <div key={c.id} className="p-8 rounded-2xl bg-gradient-to-br from-[#1C2541] to-[#0B132B] border-2 border-[#C6A15B]/30">
                  <div className="text-[#EAEAEA]/60 text-xs uppercase tracking-[0.3em] mb-2">#{i + 1}</div>
                  <div className="text-3xl font-bold truncate mb-3">{c.nom}</div>
                  <div className="text-6xl font-black text-[#FFD700] tabular-nums leading-none">
                    {fmt(c.caRealise)}
                    <span className="text-2xl text-[#EAEAEA]/60 ml-2">F</span>
                  </div>
                  <div className="text-[#EAEAEA]/60 text-sm mt-4">
                    {c.tauxRealisation || 0}% de l'objectif mensuel ({fmt(c.objectifCA)} F)
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer ticker */}
      <div className="absolute bottom-0 left-0 right-0 px-12 py-3 border-t-2 border-[#C6A15B]/30 backdrop-blur-md flex items-center justify-between">
        <div className="flex items-center gap-2 text-[#EAEAEA]/60 text-sm">
          <Clock className="w-4 h-4" />
          <span>Mise à jour automatique toutes les 15 secondes</span>
        </div>
        <div className="flex gap-2">
          <span className={`w-2 h-2 rounded-full ${view === 0 ? 'bg-[#FFD700]' : 'bg-[#EAEAEA]/20'}`} />
          <span className={`w-2 h-2 rounded-full ${view === 1 ? 'bg-[#FFD700]' : 'bg-[#EAEAEA]/20'}`} />
        </div>
      </div>
    </div>
  );
};

export default TVMode;
